<template>
  <v-card :loading="isLoading" flat class="payment-monitoring-page">
    <v-card-title class="payment-monitoring-page-header align-start flex-wrap">
      <div>
        <h1 class="payment-monitoring-page-title">Ödeme İşlemleri</h1>
        <div class="payment-monitoring-page-subtitle">
          Ödemeleri, sağlayıcı denemelerini ve son durumlarını tek yerden
          inceleyin.
        </div>
      </div>
      <v-spacer />
      <v-btn small outlined color="primary" @click="loadTransactions">
        <v-icon small left>mdi-refresh</v-icon>
        Yenile
      </v-btn>
    </v-card-title>
    <v-card-text class="pa-0">
      <div class="payment-monitoring-context-row">
        <span
          v-if="subscriptionId"
          class="payment-monitoring-context-chip payment-monitoring-context-chip--success"
        >
          Talimat: <strong>#{{ subscriptionId }}</strong>
          <v-btn
            icon
            x-small
            aria-label="Talimat filtresini kaldır"
            @click="clearSubscriptionFilter"
          >
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </span>
        <span class="payment-monitoring-context-chip">
          Seçilen tarih aralığı: <strong>{{ periodLabel }}</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Kayıt kaynağı: <strong>Robosay ve sağlayıcı olayları</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Sonucu belirsiz ödemeler: <strong>Yeni deneme kapalı</strong>
        </span>
      </div>

      <payment-monitoring-filters
        v-model="filters"
        query-label="Ödeme no, deneme no, TYA veya telefon"
        @apply="applyFilters"
      />

      <v-row dense class="transaction-metrics-row">
        <v-col
          v-for="metric in metrics"
          :key="metric.label"
          cols="12"
          sm="6"
          md="3"
        >
          <payment-monitoring-metric v-bind="metric" />
        </v-col>
      </v-row>

      <v-card
        outlined
        class="payment-monitoring-panel mb-5 payment-transaction-segments"
      >
        <div
          class="payment-monitoring-panel-header d-flex flex-wrap justify-space-between align-center"
        >
          <div>
            <div class="payment-monitoring-section-title">Ödeme durumu</div>
            <div class="payment-monitoring-section-description">
              Ödemeleri son bilinen durumuna göre filtreleyin.
            </div>
          </div>
          <v-chip small outlined>Aktif filtre: {{ activeSegmentLabel }}</v-chip>
        </div>
        <v-btn-toggle
          v-model="activeSegment"
          mandatory
          color="primary"
          class="transaction-segment-grid"
          @change="applySegment"
        >
          <v-btn
            v-for="segment in segments"
            :key="segment.id"
            :value="segment.id"
            :class="`transaction-segment transaction-segment--${segment.id}`"
          >
            <span class="transaction-segment-icon">
              <v-icon>{{ segment.icon }}</v-icon>
            </span>
            <span class="transaction-segment-copy">
              <strong>{{ segment.text }}</strong>
              <small>{{ segment.caption }}</small>
            </span>
            <b v-if="segmentValue(segment.id) !== null">
              {{ segmentValue(segment.id) }}
            </b>
          </v-btn>
        </v-btn-toggle>
      </v-card>

      <v-card outlined class="payment-monitoring-panel">
        <v-card-title class="payment-monitoring-panel-header">
          <div>
            <div class="payment-monitoring-section-title">Ödeme kayıtları</div>
            <div class="payment-monitoring-section-description">
              Bekleyen ve inceleme gerektiren kayıtları sağlayıcı sonucuyla
              birlikte inceleyin.
            </div>
          </div>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="response.items || []"
          :loading="isLoading"
          :server-items-length="response.pagination.total || 0"
          :options.sync="options"
          :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
        >
          <template v-slot:item.created_at="{ item }">
            <strong class="transaction-payment-id"
              >#{{ item.payment_id }}</strong
            >
            <div class="caption text--secondary">
              {{ formatDate(item.payment_created_at) }}
            </div>
          </template>
          <template v-slot:item.cluster_name="{ item }">
            <strong class="transaction-secondary-name">{{
              item.cluster_name
            }}</strong>
            <div class="caption text--secondary">
              TYA #{{ item.cluster_id }}
            </div>
          </template>
          <template v-slot:item.resident_name="{ item }">
            <strong class="transaction-secondary-name">{{
              item.resident_name || "-"
            }}</strong>
            <div class="caption text--secondary">
              {{ item.resident_phone || "-" }}
            </div>
          </template>
          <template v-slot:item.payment_provider_name="{ value }">
            <span class="transaction-provider-badge">{{ value }}</span>
          </template>
          <template v-slot:item.is_subscription="{ value }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="transaction-type-icon"
                  role="img"
                  tabindex="0"
                  :aria-label="value ? 'Talimat' : 'Kullanıcı'"
                >
                  <v-icon small :color="value ? 'primary' : 'grey darken-1'">
                    {{ value ? "mdi-clock-outline" : "mdi-account-outline" }}
                  </v-icon>
                </span>
              </template>
              <span>{{ value ? "Talimat" : "Kullanıcı" }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.amount="{ value }"
            ><rs-table-cell-number price :value="value"
          /></template>
          <template v-slot:item.monitoring_status="{ item, value }">
            <v-chip x-small dark :color="statusMeta(value).color">{{
              statusMeta(value).text
            }}</v-chip>
            <div
              v-if="accountingSummaryText(item)"
              class="transaction-accounting-summary"
            >
              {{ accountingSummaryText(item) }}
            </div>
          </template>
          <template v-slot:item.service_fee_invoice="{ item }">
            <v-chip
              x-small
              outlined
              :color="serviceFeeInvoiceMeta(item.service_fee_invoice).color"
            >
              {{ serviceFeeInvoiceMeta(item.service_fee_invoice).text }}
            </v-chip>
            <div
              v-if="item.service_fee_invoice"
              class="transaction-invoice-summary"
            >
              {{ moneyText(item.service_fee_invoice.invoice_amount) }}
            </div>
          </template>
          <template v-slot:item.provider_result="{ item }">
            <span class="transaction-provider-result">{{
              providerResultText(item)
            }}</span>
            <div class="caption text--secondary">
              {{
                formatDate(
                  item.last_provider_event_at || item.attempt_created_at
                )
              }}
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openDetail(item)"
              ><v-icon small>mdi-chevron-right</v-icon></v-btn
            >
          </template>
        </v-data-table>
      </v-card>
    </v-card-text>

    <v-navigation-drawer
      v-model="detailOpen"
      temporary
      fixed
      right
      width="580"
      class="payment-monitoring-drawer"
    >
      <v-card
        v-if="detailItem"
        flat
        :loading="isDetailLoading"
        class="transaction-detail-card"
      >
        <v-card-title class="transaction-detail-header">
          <div class="transaction-detail-header-copy">
            <div class="transaction-detail-title">
              Ödeme #{{ detailItem.payment_id }}
            </div>
            <div class="transaction-detail-header-meta">
              <span>
                Deneme #{{ detailItem.attempt_id || "-" }} ·
                {{ detailItem.payment_provider_name }}
              </span>
              <v-chip
                x-small
                dark
                :color="statusMeta(detailItem.monitoring_status).color"
              >
                {{ statusMeta(detailItem.monitoring_status).text }}
              </v-chip>
            </div>
          </div>
          <v-spacer />
          <v-btn icon aria-label="Detayı kapat" @click="detailOpen = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-divider />
        <v-card-text v-if="isDetailLoading" class="transaction-detail-body">
          <div class="d-flex align-center justify-center py-12">
            <v-progress-circular indeterminate color="primary" />
            <span class="ml-3">Ödeme detayı yükleniyor...</span>
          </div>
        </v-card-text>
        <v-card-text
          v-else-if="detailLoadError"
          class="transaction-detail-body"
        >
          <v-alert outlined dense type="error">
            {{ detailLoadError }}
          </v-alert>
        </v-card-text>
        <v-card-text v-else class="transaction-detail-body">
          <v-alert
            v-if="detailAlertMeta(detailItem)"
            outlined
            dense
            :type="detailAlertMeta(detailItem).type"
            class="transaction-detail-alert"
          >
            {{ detailAlertMeta(detailItem).text }}
          </v-alert>

          <section class="transaction-detail-section">
            <div class="transaction-detail-section-title">Ödeme özeti</div>
            <div class="transaction-detail-list">
              <div class="transaction-detail-row">
                <span>TYA</span>
                <strong>
                  {{ detailItem.cluster_name }} · #{{ detailItem.cluster_id }}
                </strong>
              </div>
              <div class="transaction-detail-row">
                <span>Ödeme tipi</span>
                <strong>{{
                  detailItem.is_subscription ? "Talimat" : "Kullanıcı"
                }}</strong>
              </div>
              <div class="transaction-detail-row">
                <span>{{ amountLabel(detailItem.monitoring_status) }}</span>
                <strong>
                  <rs-table-cell-number price :value="detailItem.amount" />
                </strong>
              </div>
              <div class="transaction-detail-row">
                <span>TYA payı</span>
                <strong>
                  <rs-table-cell-number
                    price
                    :value="detailItem.amount_for_cluster"
                  />
                </strong>
              </div>
              <div class="transaction-detail-row">
                <span>Borç işlemleri</span>
                <strong>{{
                  paymentEffectsText(detailItem.payment_effects_status)
                }}</strong>
              </div>
              <div class="transaction-detail-row">
                <span>Ödeme zamanı</span>
                <strong>{{ formatDate(detailItem.payment_created_at) }}</strong>
              </div>
              <div v-if="detailItem.success_at" class="transaction-detail-row">
                <span>Başarılı oldu</span>
                <strong>{{ formatDate(detailItem.success_at) }}</strong>
              </div>
              <div
                v-if="detailItem.payment_effects_applied_at"
                class="transaction-detail-row"
              >
                <span>Sisteme işlendi</span>
                <strong>{{
                  formatDate(detailItem.payment_effects_applied_at)
                }}</strong>
              </div>
            </div>
          </section>

          <section
            v-if="detailItem.success_at && !isDetailLoading"
            class="transaction-detail-section"
          >
            <div class="transaction-accounting-heading">
              <div class="transaction-detail-section-title mb-0">
                Robosay kayıtları
              </div>
              <div class="transaction-accounting-actions">
                <router-link
                  v-if="can('see-house-income')"
                  :to="{
                    name: 'incomes.residents.show',
                    params: { id: detailItem.resident_id },
                  }"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon x-small color="primary" class="mr-1"
                    >mdi-open-in-new</v-icon
                  >Mali işlemler
                </router-link>
              </div>
            </div>
            <div
              v-if="!(detailItem.collectings || []).length"
              class="transaction-detail-empty transaction-detail-empty--warning"
            >
              Bu başarılı ödeme için tahsilat kaydı bulunamadı.
            </div>
            <article
              v-for="collecting in detailItem.collectings || []"
              :key="collecting.collecting_id"
              class="transaction-accounting-card"
            >
              <header class="transaction-accounting-card-header">
                <div>
                  <strong>Tahsilat #{{ collecting.collecting_id }}</strong>
                  <span>
                    <template v-if="collecting.collecting_number">
                      {{ collecting.collecting_number }} ·
                    </template>
                    {{ formatDate(collecting.collected_on) }}
                  </span>
                </div>
                <rs-table-cell-number
                  v-if="collecting.amount !== null"
                  price
                  :value="collecting.amount"
                />
              </header>

              <div class="transaction-accounting-meta">
                <span>
                  BB:
                  <router-link
                    v-if="collecting.house_id && can('see-house-income')"
                    :to="{
                      name: 'incomes.houses.show',
                      params: { id: collecting.house_id },
                    }"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <v-icon x-small color="primary" class="mr-1"
                      >mdi-open-in-new</v-icon
                    >
                    {{ collecting.block_name || "-" }}-{{
                      collecting.door_number || "-"
                    }}
                  </router-link>
                  <strong v-else>
                    {{ collecting.block_name || "-" }}-{{
                      collecting.door_number || "-"
                    }}
                  </strong>
                </span>
                <v-chip
                  x-small
                  outlined
                  :color="
                    collecting.balance_activity_exists ? 'success' : 'error'
                  "
                >
                  {{
                    collecting.balance_activity_exists
                      ? "Bakiye hareketi oluştu"
                      : "Bakiye hareketi yok"
                  }}
                </v-chip>
              </div>

              <div class="transaction-accounting-debts">
                <div class="transaction-accounting-debts-title">
                  Kapatılan borçlar
                </div>
                <div
                  v-if="!collecting.debt_allocations.length"
                  class="transaction-accounting-debt-empty"
                >
                  Borç kapatılmadı.
                </div>
                <div
                  v-for="allocation in collecting.debt_allocations"
                  :key="allocation.debt_collecting_id"
                  class="transaction-accounting-debt"
                >
                  <div>
                    <strong>Borç #{{ allocation.debt_id }}</strong>
                    <span v-if="allocation.debt_number">
                      {{ allocation.debt_number }}
                    </span>
                    <small>{{ allocation.debt_description || "-" }}</small>
                  </div>
                  <rs-table-cell-number
                    price
                    :value="allocation.total_amount"
                  />
                </div>
                <div
                  v-if="collecting.unallocated_amount > 0"
                  class="transaction-accounting-advance"
                >
                  <span>Avans olarak işlendi</span>
                  <strong>
                    <rs-table-cell-number
                      price
                      :value="collecting.unallocated_amount"
                    />
                  </strong>
                </div>
              </div>
            </article>
          </section>

          <section class="transaction-detail-section">
            <div class="transaction-accounting-heading">
              <div class="transaction-detail-section-title mb-0">
                Hizmet bedeli faturası
              </div>
              <div class="transaction-accounting-actions">
                <a
                  v-if="
                    detailItem.service_fee_invoice &&
                    detailItem.service_fee_invoice.document_url
                  "
                  :href="detailItem.service_fee_invoice.document_url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon x-small color="primary" class="mr-1"
                    >mdi-open-in-new</v-icon
                  >Faturayı görüntüle
                </a>
              </div>
            </div>
            <div
              v-if="!detailItem.service_fee_invoice"
              class="transaction-detail-empty"
            >
              Bu ödeme için hizmet bedeli faturası henüz oluşmadı.
            </div>
            <template v-else>
              <div class="transaction-detail-list">
                <div class="transaction-detail-row">
                  <span>Durum</span>
                  <div class="transaction-detail-value">
                    <v-chip
                      x-small
                      outlined
                      :color="
                        serviceFeeInvoiceMeta(detailItem.service_fee_invoice)
                          .color
                      "
                    >
                      {{
                        serviceFeeInvoiceMeta(detailItem.service_fee_invoice)
                          .text
                      }}
                    </v-chip>
                  </div>
                </div>
                <div class="transaction-detail-row transaction-detail-row--top">
                  <span>Paraşüt tahsilatı</span>
                  <div class="transaction-detail-value">
                    <v-chip
                      x-small
                      outlined
                      :color="
                        serviceFeeCollectionMeta(
                          detailItem.service_fee_invoice
                            .parasut_collection_status
                        ).color
                      "
                    >
                      {{
                        serviceFeeCollectionMeta(
                          detailItem.service_fee_invoice
                            .parasut_collection_status
                        ).text
                      }}
                    </v-chip>
                    <div
                      v-if="
                        detailItem.service_fee_invoice
                          .parasut_collection_status === 'manual_review' &&
                        (detailItem.service_fee_invoice
                          .parasut_collection_blocked_reason ||
                          detailItem.service_fee_invoice
                            .parasut_collection_error)
                      "
                      class="transaction-detail-note error--text mt-1"
                    >
                      {{
                        detailItem.service_fee_invoice
                          .parasut_collection_blocked_reason ||
                        detailItem.service_fee_invoice.parasut_collection_error
                      }}
                    </div>
                  </div>
                </div>
                <div class="transaction-detail-row">
                  <span>Referans</span>
                  <strong>{{
                    detailItem.service_fee_invoice.invoice_reference || "-"
                  }}</strong>
                </div>
                <div class="transaction-detail-row">
                  <span>Fatura tutarı</span>
                  <strong>
                    <rs-table-cell-number
                      price
                      :value="detailItem.service_fee_invoice.invoice_amount"
                    />
                  </strong>
                </div>
                <div
                  v-if="
                    detailItem.service_fee_invoice.parasut_collection_amount !==
                      null &&
                    detailItem.service_fee_invoice.parasut_collection_amount !==
                      undefined
                  "
                  class="transaction-detail-row"
                >
                  <span>Paraşüt tahsilat tutarı</span>
                  <strong>
                    <rs-table-cell-number
                      price
                      :value="
                        detailItem.service_fee_invoice.parasut_collection_amount
                      "
                    />
                  </strong>
                </div>
                <div class="transaction-detail-row">
                  <span>Paraşüt fatura no</span>
                  <strong>{{
                    detailItem.service_fee_invoice.parasut_invoice_no || "-"
                  }}</strong>
                </div>
                <div
                  v-if="
                    detailItem.service_fee_invoice.last_error ||
                    detailItem.service_fee_invoice.blocked_reason
                  "
                  class="transaction-detail-row transaction-detail-row--top"
                >
                  <span>Son hata</span>
                  <div class="transaction-detail-value">
                    {{
                      detailItem.service_fee_invoice.last_error ||
                      detailItem.service_fee_invoice.blocked_reason
                    }}
                  </div>
                </div>
              </div>
            </template>
          </section>

          <section class="transaction-detail-section">
            <div class="transaction-detail-section-title">
              Sağlayıcı ve hakediş
            </div>
            <div class="transaction-detail-list">
              <div class="transaction-detail-row">
                <span>Sağlayıcı sipariş no</span>
                <strong>{{ detailItem.provider_order_id || "-" }}</strong>
              </div>
              <div class="transaction-detail-row">
                <span>Sağlayıcı işlem no</span>
                <strong>{{ detailItem.provider_transaction_id || "-" }}</strong>
              </div>
              <div class="transaction-detail-row transaction-detail-row--top">
                <span>Sağlayıcı Hakediş Onayı</span>
                <div class="transaction-detail-value">
                  <v-chip
                    x-small
                    dark
                    :color="
                      marketplaceApprovalMeta(detailItem.marketplace_approval)
                        .color
                    "
                  >
                    {{
                      marketplaceApprovalMeta(detailItem.marketplace_approval)
                        .text
                    }}
                  </v-chip>
                  <div
                    v-if="
                      marketplaceApprovalAt(detailItem.marketplace_approval)
                    "
                    class="caption text--secondary mt-1"
                  >
                    {{
                      formatDate(
                        marketplaceApprovalAt(detailItem.marketplace_approval)
                      )
                    }}
                  </div>
                  <div class="transaction-detail-note">
                    Onaylanmadan Ödeme Sağlayıcı Tarafından TYA Hesabına
                    Aktarılmaz
                  </div>
                </div>
              </div>
              <div
                v-if="detailItem.provider_blockage_resolved_at"
                class="transaction-detail-row transaction-detail-row--top"
              >
                <span>Tahmini hakediş aktarım tarihi</span>
                <div class="transaction-detail-value">
                  <strong>{{
                    formatDate(detailItem.provider_blockage_resolved_at)
                  }}</strong>
                  <div class="transaction-detail-note">
                    Bu tarih tahminidir. Banka hesabına aktarım teyidi değildir.
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="transaction-detail-section">
            <div class="transaction-detail-section-title">
              Sağlayıcı denemeleri
            </div>
            <div
              v-if="!(detailItem.attempts || []).length"
              class="transaction-detail-empty"
            >
              Sağlayıcı denemesi bulunamadı.
            </div>
            <div
              v-for="attempt in detailItem.attempts || []"
              :key="attempt.id"
              class="transaction-attempt-card"
            >
              <div>
                <strong
                  >Deneme #{{ attempt.id }} · {{ attempt.attempt_type }}</strong
                >
                <div class="transaction-attempt-description">
                  {{
                    attempt.status_reason || "Sağlayıcı açıklaması bulunmuyor"
                  }}
                </div>
              </div>
              <v-chip x-small dark :color="statusMeta(attempt.status).color">
                {{ statusMeta(attempt.status).text }}
              </v-chip>
            </div>
          </section>

          <section class="transaction-detail-section">
            <div class="transaction-detail-section-title">İşlem geçmişi</div>
            <div
              v-if="!transactionTimeline(detailItem).length"
              class="transaction-detail-empty"
            >
              İşlem geçmişi bulunamadı.
            </div>
            <div v-else class="transaction-detail-timeline">
              <div
                v-for="event in transactionTimeline(detailItem)"
                :key="event.key"
                class="transaction-detail-timeline-item"
              >
                <span
                  class="transaction-detail-timeline-dot"
                  :class="{
                    'transaction-detail-timeline-dot--error':
                      event.error || event.processing_error,
                    'transaction-detail-timeline-dot--success':
                      !event.error &&
                      !event.processing_error &&
                      (event.success || event.is_effective),
                  }"
                />
                <div class="transaction-detail-timeline-copy">
                  <strong>{{ event.text }}</strong>
                  <span v-if="event.at">{{ formatDate(event.at) }}</span>
                  <span v-if="event.processing_error" class="error--text">
                    {{ event.processing_error }}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </v-card-text>
        <v-card-actions class="transaction-detail-footer">
          <span
            v-if="canQueryStatus(detailItem) || canManuallyResolve(detailItem)"
            class="transaction-detail-footer-note"
          >
            {{
              canManuallyResolve(detailItem)
                ? "Provider panelinde doğrulanan sonucu seçin."
                : "Durum kontrolü yeni ödeme başlatmaz."
            }}
          </span>
          <v-spacer />
          <v-btn text @click="detailOpen = false">Kapat</v-btn>
          <v-btn
            v-if="canQueryStatus(detailItem)"
            color="primary"
            :loading="isStatusQueryLoading"
            @click="queryStatus"
          >
            <v-icon small left>mdi-magnify-scan</v-icon>
            Durumu Sorgula
          </v-btn>
          <v-btn
            v-if="canManuallyResolve(detailItem)"
            color="warning"
            dark
            :loading="isManualResolutionLoading"
            @click="resolveManualReview"
          >
            <v-icon small left>mdi-check-decagram-outline</v-icon>
            Manuel Sonuçlandır
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-navigation-drawer>
  </v-card>
</template>

<script>
import Swal from "sweetalert2";
import { hasPermissions, isPage } from "@/view/mixins";
import PaymentMonitoringFilters from "./components/PaymentMonitoringFilters.vue";
import PaymentMonitoringMetric from "./components/PaymentMonitoringMetric.vue";
import {
  buildPaymentMonitoringParams,
  formatMonitoringDate,
  formatMonitoringPercent,
  paymentMonitoringFiltersFromQuery,
  paymentMonitoringOptionsFromQuery,
  syncPaymentMonitoringUrl,
} from "./paymentMonitoring";

const emptyResponse = () => ({
  meta: { period: {} },
  summary: {},
  items: [],
  pagination: { total: 0 },
});

const transactionSegmentIds = ["all", "success", "failed", "attention"];

export default {
  name: "PaymentTransactions",
  components: { PaymentMonitoringFilters, PaymentMonitoringMetric },
  mixins: [hasPermissions, isPage],
  data() {
    const routeSegment = Array.isArray(this.$route.query.segment)
      ? this.$route.query.segment[0]
      : this.$route.query.segment;

    return {
      pageMeta: { title: "Ödeme İşlemleri" },
      isLoading: false,
      isDetailLoading: false,
      detailLoadError: null,
      isStatusQueryLoading: false,
      isManualResolutionLoading: false,
      detailOpen: false,
      detailItem: null,
      subscriptionId:
        Number.parseInt(this.$route.query.subscription_id, 10) || null,
      filters: paymentMonitoringFiltersFromQuery(this.$route.query),
      response: emptyResponse(),
      activeSegment: transactionSegmentIds.includes(routeSegment)
        ? routeSegment
        : "all",
      options: paymentMonitoringOptionsFromQuery(this.$route.query, {
        page: 1,
        itemsPerPage: 20,
        sortBy: ["created_at"],
        sortDesc: [true],
      }),
      segments: [
        {
          id: "all",
          text: "Tümü",
          caption: "Seçilen dönemdeki ödemeler",
          icon: "mdi-view-grid-outline",
          statuses: [],
        },
        {
          id: "success",
          text: "Başarılı",
          caption: "Tahsilatı tamamlandı",
          icon: "mdi-check-circle-outline",
          statuses: ["success"],
        },
        {
          id: "failed",
          text: "Başarısız",
          caption: "Ödeme alınamadı veya iade",
          icon: "mdi-close-circle-outline",
          statuses: ["failed", "refunded"],
        },
        {
          id: "attention",
          text: "Takip gereken",
          caption: "Bekleyen veya inceleme",
          icon: "mdi-alert-outline",
          statuses: ["pending", "unknown", "manual_review"],
        },
      ],
      headers: [
        { text: "Ödeme", value: "created_at", width: "150px" },
        { text: "TYA", value: "cluster_name" },
        { text: "POS", value: "payment_provider_name", width: "90px" },
        {
          text: "Tip",
          value: "is_subscription",
          align: "center",
          width: "64px",
        },
        { text: "Sakin", value: "resident_name" },
        { text: "Tutar", value: "amount", align: "end" },
        { text: "Durum", value: "monitoring_status" },
        {
          text: "Hizmet faturası",
          value: "service_fee_invoice",
          sortable: false,
          width: "130px",
        },
        {
          text: "Sağlayıcı sonucu",
          value: "provider_result",
          sortable: false,
        },
        { text: "", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    metrics() {
      const value = this.response.summary || {};
      return [
        {
          label: "Toplam ödeme",
          value: value.payment_count,
          caption: "Seçilen dönemde oluşturulan",
          icon: "mdi-receipt-text-outline",
          color: "primary",
          footLabel: "Karttan çekilen",
          footValue: this.moneyText(value.successful_amount),
        },
        {
          label: "Başarılı",
          value: value.successful_count,
          caption: "Tahsilatı tamamlanan",
          icon: "mdi-check-circle-outline",
          color: "success",
          footLabel: "Başarı oranı",
          footValue: formatMonitoringPercent(value.success_rate),
        },
        {
          label: "Bekleyen",
          value: value.pending_count,
          caption: "Sonucu henüz kesinleşmeyen",
          icon: "mdi-clock-outline",
          color: "warning",
          footLabel: "Sonuç belirsiz",
          footValue: value.unknown_count || 0,
        },
        {
          label: "İnceleme gerekli",
          value: value.manual_review_count,
          caption: "Otomatik sonuç alınamayan",
          icon: "mdi-magnify-scan",
          color: "error",
          footLabel: "Toplam takip",
          footValue: value.attention_count || 0,
        },
      ];
    },
    activeSegmentLabel() {
      return (
        this.segments.find((item) => item.id === this.activeSegment)?.text ||
        "Tümü"
      );
    },
    periodLabel() {
      const period = this.response.meta?.period || {};
      return period.min && period.max ? `${period.min} - ${period.max}` : "-";
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.syncUrlState();
        this.loadTransactions();
      },
    },
  },
  mounted() {
    this.syncUrlState();
    this.loadTransactions();
  },
  methods: {
    applyFilters() {
      if (this.options.page !== 1) this.options.page = 1;
      else {
        this.syncUrlState();
        this.loadTransactions();
      }
    },
    applySegment() {
      this.applyFilters();
    },
    syncUrlState() {
      syncPaymentMonitoringUrl(this.filters, this.options, {
        segment: this.activeSegment,
        subscription_id: this.subscriptionId,
      });
    },
    segmentValue(segmentId) {
      const value = this.response.summary || {};
      return {
        all: value.payment_count || 0,
        success: value.successful_count || 0,
        failed: value.failed_count || 0,
        attention: value.attention_count || 0,
      }[segmentId];
    },
    loadTransactions() {
      if (this.isLoading) return;
      this.isLoading = true;
      const segment = this.segments.find(
        (item) => item.id === this.activeSegment
      );
      const params = buildPaymentMonitoringParams(this.filters, {
        page: this.options.page,
        per_page: this.options.itemsPerPage,
        order_by: this.options.sortBy[0] || "created_at",
        order: this.options.sortDesc[0] ? "desc" : "asc",
      });
      if (segment?.statuses.length) params.statuses = segment.statuses;
      if (this.subscriptionId) {
        params.user_card_subscription_id = this.subscriptionId;
      }

      this.$api
        .query("api-super-admin/payment-monitoring/transactions", { params })
        .then(
          (response) => (this.response = response.data.data || emptyResponse())
        )
        .catch((error) => this.handleError(error))
        .finally(() => (this.isLoading = false));
    },
    clearSubscriptionFilter() {
      this.subscriptionId = null;
      this.applyFilters();
    },
    openDetail(item) {
      this.detailOpen = true;
      this.detailItem = item;
      this.detailLoadError = null;
      this.loadDetail(item.payment_id);
    },
    loadDetail(paymentId) {
      this.isDetailLoading = true;
      this.detailLoadError = null;
      this.$api
        .query(`api-super-admin/payment-monitoring/transactions/${paymentId}`)
        .then(
          (response) =>
            (this.detailItem = response.data.data?.item || this.detailItem)
        )
        .catch((error) => {
          this.detailLoadError =
            "Ödeme detayı yüklenemedi. Lütfen yeniden deneyin.";
          this.handleError(error);
        })
        .finally(() => (this.isDetailLoading = false));
    },
    queryStatus() {
      if (
        !window.confirm(
          "Bu işlem yeni ödeme başlatmaz. Mevcut ödeme sonucu sağlayıcıdan kontrol edilsin mi?"
        )
      )
        return;
      this.isStatusQueryLoading = true;
      this.$api
        .post(
          `api-super-admin/payment-ops/payments/${this.detailItem.payment_id}/status-query`
        )
        .then(() => {
          this.$toast.success("Sağlayıcı durum sorgusu tamamlandı.");
          this.loadDetail(this.detailItem.payment_id);
          this.loadTransactions();
        })
        .catch((error) => this.handleError(error))
        .finally(() => (this.isStatusQueryLoading = false));
    },
    async resolveManualReview() {
      const result = await Swal.fire({
        title: `Ödeme #${this.detailItem.payment_id} manuel sonuçlandırılsın mı?`,
        text: `Provider panelinde doğruladığınız sonucu seçin. İşlem: ${
          this.detailItem.provider_transaction_id ||
          this.detailItem.provider_order_id ||
          "-"
        }, tutar: ${this.moneyText(
          this.detailItem.amount
        )}. Başarılı sonuç tahsilat ve borç kayıtlarını uygulayabilir.`,
        input: "radio",
        inputOptions: {
          success: "Başarılı olarak tamamla",
          failed: "Başarısız olarak kapat",
        },
        inputValidator: (value) =>
          value ? null : "Lütfen doğruladığınız sonucu seçin.",
        showCancelButton: true,
        confirmButtonText: "Sonuçlandır",
        cancelButtonText: "Vazgeç",
        reverseButtons: true,
      });

      if (!result.value) return;

      this.isManualResolutionLoading = true;
      this.$api
        .post(
          `api-super-admin/payment-ops/payments/${this.detailItem.payment_id}/manual-resolution`,
          {
            attempt_id: this.detailItem.attempt_id,
            outcome: result.value,
            confirmed: true,
          }
        )
        .then(() => {
          this.$toast.success(
            result.value === "success"
              ? "Ödeme başarılı olarak sonuçlandırıldı."
              : "Ödeme başarısız olarak sonuçlandırıldı."
          );
          this.loadDetail(this.detailItem.payment_id);
          this.loadTransactions();
        })
        .catch((error) => this.handleError(error))
        .finally(() => (this.isManualResolutionLoading = false));
    },
    isChargeBlocking(status) {
      return ["pending", "unknown", "manual_review"].includes(status);
    },
    canQueryStatus(item) {
      return ["pending", "unknown"].includes(
        item.attempt_status || item.monitoring_status
      );
    },
    canManuallyResolve(item) {
      return item.attempt_status === "manual_review";
    },
    detailAlertMeta(item) {
      if (this.canManuallyResolve(item)) {
        return {
          type: "warning",
          text: "Sağlayıcı sonucu otomatik olarak kesinleştirilemedi. Provider panelinden doğruladıktan sonra manuel sonuçlandırın.",
        };
      }

      if (this.isChargeBlocking(item.monitoring_status)) {
        return {
          type: "warning",
          text: "Sonuç kesinleşmeden aynı borç için yeni ödeme başlatılmamalıdır. Sistem mevcut ödeme sonucunu takip etmeye devam eder.",
        };
      }

      if (item.monitoring_status === "success") {
        return item.payment_effects_status === "applied"
          ? {
              type: "success",
              text: "Ödeme tamamlandı ve borç işlemleri uygulandı.",
            }
          : {
              type: "info",
              text: "Ödeme sağlayıcı tarafında başarılı. Borç işlemlerinin güncel durumunu aşağıdan inceleyin.",
            };
      }

      if (item.monitoring_status === "failed") {
        return {
          type: "error",
          text: "Ödeme tamamlanmadı. Sağlayıcı sonucunu ve deneme geçmişini aşağıdan inceleyin.",
        };
      }

      if (item.monitoring_status === "refunded") {
        return {
          type: "info",
          text: "Ödeme iade edildi. İade ve borç işlemlerinin durumunu aşağıdan inceleyin.",
        };
      }

      return null;
    },
    amountLabel(status) {
      return ["success", "refunded"].includes(status)
        ? "Karttan çekilen"
        : "Karttan çekilecek";
    },
    accountingSummaryText(item) {
      if (!item.success_at) return null;
      if (item.payment_effects_status !== "applied") {
        return (
          "Sistem: " + this.paymentEffectsText(item.payment_effects_status)
        );
      }
      if (!item.collecting_count) return "Tahsilat kaydı yok";
      if (!item.actual_debt_count) {
        return [item.collecting_count, " tahsilat · Avans"].join("");
      }

      return [
        item.collecting_count,
        " tahsilat · ",
        item.actual_debt_count,
        " borç",
      ].join("");
    },
    paymentEffectsText(status) {
      return (
        {
          pending: "Bekliyor",
          applying: "İşleniyor",
          applied: "Tamamlandı",
          failed: "Başarısız",
          manual_review: "İnceleme gerekli",
        }[status] ||
        status ||
        "-"
      );
    },
    serviceFeeInvoiceMeta(invoice) {
      if (!invoice) return { text: "Yok", color: "grey" };

      if (invoice.delivery_status === "failed") {
        return { text: "Bildirim başarısız", color: "error" };
      }

      if (invoice.delivery_status === "manual_review") {
        return { text: "Bildirim kontrol gerekli", color: "error" };
      }

      return (
        {
          pending: { text: "Bekleniyor", color: "grey" },
          ready: { text: "Hazır", color: "primary" },
          provider_blocked: { text: "Sağlayıcı bekleniyor", color: "warning" },
          parasut_create_in_progress: {
            text: "Paraşüte gönderiliyor",
            color: "warning",
          },
          sent_to_parasut: { text: "Paraşüte gönderildi", color: "primary" },
          registration_create_in_progress: {
            text: "Resmileştirme başlatılıyor",
            color: "warning",
          },
          registration_pending: { text: "Resmileşiyor", color: "warning" },
          registration_running: { text: "Resmileşiyor", color: "warning" },
          registered: { text: "Resmileşti", color: "success" },
          pdf_ready: { text: "Fatura hazır", color: "success" },
          delivered: { text: "Gönderildi", color: "success" },
          failed: { text: "Başarısız", color: "error" },
          manual_review: { text: "Kontrol gerekli", color: "error" },
        }[invoice.status] || { text: invoice.status || "-", color: "grey" }
      );
    },
    serviceFeeProviderGateMeta(status) {
      return (
        {
          ready: {
            text: "Sağlayıcı kontrolü tamamlandı",
            success: true,
          },
          not_required: {
            text: "Sağlayıcı kontrolü gerekmiyor",
            success: true,
          },
          waiting: {
            text: "Sağlayıcı kontrolü bekleniyor",
          },
          blocked: {
            text: "Sağlayıcı kontrolü başarısız",
            error: true,
          },
        }[status] || { text: "Sağlayıcı kontrolü" }
      );
    },
    serviceFeeCollectionMeta(status) {
      return (
        {
          pending: { text: "Bekliyor", color: "grey" },
          create_in_progress: { text: "İşleniyor", color: "warning" },
          collected: { text: "Tahsil edildi", color: "success" },
          manual_review: { text: "Kontrol gerekli", color: "error" },
        }[status] || { text: "Henüz başlatılmadı", color: "grey" }
      );
    },
    transactionTimeline(item) {
      const paymentEvents = [...(item.events || [])]
        .sort((left, right) => {
          const leftAt = left.processed_at || left.created_at;
          const rightAt = right.processed_at || right.created_at;

          return new Date(leftAt).getTime() - new Date(rightAt).getTime();
        })
        .map((event) => ({
          ...event,
          key: `payment-${event.id}`,
          text: this.eventText(event.event_type),
          at: event.processed_at || event.created_at,
        }));
      const invoice = item.service_fee_invoice;
      if (!invoice) return paymentEvents.reverse();
      const providerGate = this.serviceFeeProviderGateMeta(
        invoice.provider_gate_status
      );

      const invoiceEvents = [
        invoice.created_at && {
          key: "invoice-candidate",
          text: "Fatura adayı oluşturuldu",
          at: invoice.created_at,
          success: true,
        },
        invoice.provider_verified_at && {
          key: "invoice-provider-gate",
          text: providerGate.text,
          at: invoice.provider_verified_at,
          success: providerGate.success,
          error: providerGate.error,
        },
        invoice.parasut_sales_invoice_id && {
          key: "invoice-draft",
          text: "Paraşüt taslağı oluşturuldu",
          at: null,
          success: true,
        },
        invoice.parasut_trackable_job_id && {
          key: "invoice-registration",
          text: "E-Arşiv işlemi başlatıldı",
          at: null,
          success: true,
        },
        invoice.parasut_registered_at && {
          key: "invoice-registered",
          text: "E-Arşiv resmileşti",
          at: invoice.parasut_registered_at,
          success: true,
        },
        invoice.document_id && {
          key: "invoice-pdf",
          text: "PDF fatura saklandı",
          at: invoice.parasut_registered_at,
          success: true,
        },
        invoice.parasut_collected_at && {
          key: "invoice-collection",
          text: "Fatura tahsilatı Paraşüt’e işlendi",
          at: invoice.parasut_collected_at,
          success: true,
        },
        ["sent", "delivered"].includes(invoice.delivery_status) && {
          key: "invoice-delivery-sent",
          text: "Kullanıcı bildirimi kuyruğa alındı",
          at: invoice.delivery_status === "sent" ? invoice.updated_at : null,
          success: true,
        },
        invoice.delivery_status === "delivered" && {
          key: "invoice-delivery-completed",
          text: "Fatura gönderildi",
          at: invoice.delivered_at || invoice.updated_at,
          success: true,
        },
        invoice.delivery_status === "failed" && {
          key: "invoice-delivery-failed",
          text: "Kullanıcı bildirimi başarısız",
          at: invoice.delivery_failed_at,
          error: true,
        },
      ].filter(Boolean);

      return [...paymentEvents, ...invoiceEvents].reverse();
    },
    statusMeta(status) {
      return (
        {
          created: { text: "Bekleyen", color: "warning" },
          pending: { text: "Bekleyen", color: "warning" },
          success: { text: "Başarılı", color: "success" },
          failed: { text: "Başarısız", color: "error" },
          cancelled: { text: "İptal", color: "grey" },
          refunded: { text: "İade", color: "info" },
          unknown: { text: "Sonuç belirsiz", color: "info" },
          manual_review: { text: "İnceleme gerekli", color: "error" },
        }[status] || { text: status || "-", color: "grey" }
      );
    },
    marketplaceApprovalMeta(approval) {
      const status = approval?.status;

      return (
        {
          created: { text: "Onay bekliyor", color: "warning" },
          pending: { text: "Onay bekliyor", color: "warning" },
          success: { text: "Onaylandı", color: "success" },
          failed: { text: "Onaylanamadı", color: "error" },
          unknown: { text: "Sonuç belirsiz", color: "info" },
          manual_review: { text: "İnceleme gerekli", color: "error" },
          not_required: { text: "Onay gerekmiyor", color: "grey" },
        }[status] || { text: "Onay kaydı yok", color: "grey" }
      );
    },
    marketplaceApprovalAt(approval) {
      if (!approval) return null;

      return (
        approval.succeeded_at ||
        approval.last_attempted_at ||
        approval.updated_at ||
        approval.created_at
      );
    },
    providerResultText(item) {
      if (
        item.provider_result &&
        item.provider_result.toLowerCase() === "success"
      ) {
        return "Onaylandı";
      }

      return item.provider_result || this.eventText(item.last_provider_event);
    },
    eventText(event) {
      return (
        {
          create_response: "Ödeme yanıtı alındı",
          callback_success: "Başarılı sağlayıcı bildirimi",
          callback_fail: "Başarısız sağlayıcı bildirimi",
          status_query: "Durum sorgulandı",
          client_browser_completed: "3D ekranı tamamlandı",
          client_browser_closed: "3D ekranı kapatıldı",
          client_browser_error: "3D ekran hatası",
        }[event] ||
        event ||
        "-"
      );
    },
    moneyText(value) {
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
      }).format(Number(value || 0));
    },
    formatDate: formatMonitoringDate,
  },
};
</script>

<style scoped>
.transaction-metrics-row {
  margin-bottom: 18px;
}

.payment-transaction-segments {
  background: #ffffff;
}

.transaction-segment-grid {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-radius: 0;
}

.transaction-segment-grid ::v-deep .v-btn {
  width: 100%;
  min-width: 0 !important;
  min-height: 86px;
  height: auto !important;
  padding: 14px 16px !important;
  border: 0 !important;
  border-left: 4px solid #5867dd !important;
  border-radius: 0 !important;
  background: #ffffff !important;
  color: #3f4254 !important;
  opacity: 1 !important;
  text-transform: none;
}

.transaction-segment-grid ::v-deep .v-btn + .v-btn {
  border-left-width: 4px !important;
}

.transaction-segment-grid ::v-deep .v-btn::before {
  background: #f4f6ff;
}

.transaction-segment-grid ::v-deep .v-btn--active {
  background: #f8f9ff !important;
}

.transaction-segment-grid ::v-deep .v-btn__content {
  width: 100%;
  justify-content: flex-start;
  gap: 12px;
}

.transaction-segment--success {
  border-left-color: #1bc5bd !important;
}

.transaction-segment--failed {
  border-left-color: #f64e60 !important;
}

.transaction-segment--attention {
  border-left-color: #ffa800 !important;
}

.transaction-segment-icon {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f0f2ff;
}

.transaction-segment-icon .v-icon {
  color: #5867dd;
  font-size: 19px;
}

.transaction-segment--success .transaction-segment-icon {
  background: rgba(27, 197, 189, 0.11);
}

.transaction-segment--success .transaction-segment-icon .v-icon {
  color: #0f9f97;
}

.transaction-segment--failed .transaction-segment-icon {
  background: rgba(246, 78, 96, 0.1);
}

.transaction-segment--failed .transaction-segment-icon .v-icon {
  color: #f64e60;
}

.transaction-segment--attention .transaction-segment-icon {
  background: rgba(255, 168, 0, 0.11);
}

.transaction-segment--attention .transaction-segment-icon .v-icon {
  color: #d99000;
}

.transaction-segment-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: flex-start;
  flex-direction: column;
  text-align: left;
}

.transaction-segment-copy strong {
  color: #181c32;
  font-size: 12px;
}

.transaction-segment-copy small {
  margin-top: 2px;
  overflow: hidden;
  color: #7e8299;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.transaction-segment-grid b {
  color: #181c32;
  font-size: 20px;
  font-weight: 800;
}

.transaction-payment-id {
  color: #181c32;
}

.transaction-secondary-name {
  font-weight: 600;
}

.transaction-provider-badge {
  display: inline-flex;
  min-height: 22px;
  max-width: 76px;
  align-items: center;
  padding: 2px 7px;
  overflow: hidden;
  border-radius: 4px;
  background: #f5f7fa;
  color: #5e6278;
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.transaction-type-icon {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f5f7fa;
}

.transaction-type-icon:focus {
  outline: 2px solid #5867dd;
  outline-offset: 2px;
}

.transaction-provider-result {
  display: block;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.transaction-accounting-summary {
  margin-top: 4px;
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.transaction-invoice-summary {
  margin-top: 4px;
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.transaction-detail-card {
  display: flex;
  min-height: 100%;
  height: 100%;
  flex-direction: column;
  overflow: hidden;
}

.transaction-detail-header {
  flex: 0 0 auto;
  gap: 14px;
  padding: 18px 20px !important;
  background: #ffffff;
}

.transaction-detail-header-copy {
  min-width: 0;
}

.transaction-detail-title {
  color: #181c32;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.25;
}

.transaction-detail-header-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
}

.transaction-detail-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 20px !important;
}

.transaction-detail-alert {
  margin-bottom: 22px;
  font-size: 12px;
  line-height: 1.5;
}

.transaction-detail-section + .transaction-detail-section {
  margin-top: 22px;
}

.transaction-detail-section-title {
  margin-bottom: 9px;
  color: #7e8299;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.transaction-detail-list {
  overflow: hidden;
  border: 1px solid #e4e6ef;
  border-radius: 4px;
  background: #ffffff;
}

.transaction-detail-row {
  display: flex;
  min-height: 45px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 13px;
  border-bottom: 1px solid #eff1f5;
}

.transaction-detail-row:last-child {
  border-bottom: 0;
}

.transaction-detail-row--top {
  align-items: flex-start;
}

.transaction-detail-row > span {
  flex: 0 0 42%;
  color: #7e8299;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
}

.transaction-detail-row > strong,
.transaction-detail-value {
  min-width: 0;
  color: #3f4254;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  text-align: right;
  word-break: break-word;
}

.transaction-detail-value {
  display: flex;
  flex: 1;
  align-items: flex-end;
  flex-direction: column;
}

.transaction-detail-note {
  max-width: 280px;
  margin-top: 5px;
  color: #a1a5b7;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.45;
}

.transaction-accounting-heading,
.transaction-accounting-actions,
.transaction-accounting-card-header,
.transaction-accounting-meta,
.transaction-accounting-debt,
.transaction-accounting-advance {
  display: flex;
  align-items: center;
}

.transaction-accounting-heading,
.transaction-accounting-card-header,
.transaction-accounting-meta,
.transaction-accounting-debt,
.transaction-accounting-advance {
  justify-content: space-between;
}

.transaction-accounting-heading {
  gap: 12px;
  margin-bottom: 9px;
}

.transaction-accounting-actions {
  gap: 9px;
  font-size: 10px;
  font-weight: 700;
}

.transaction-accounting-card {
  overflow: hidden;
  border: 1px solid #e4e6ef;
  border-radius: 4px;
  background: #ffffff;
}

.transaction-accounting-card + .transaction-accounting-card {
  margin-top: 10px;
}

.transaction-accounting-card-header {
  gap: 16px;
  padding: 12px 13px;
}

.transaction-accounting-card-header strong,
.transaction-accounting-card-header span,
.transaction-accounting-debt strong,
.transaction-accounting-debt span,
.transaction-accounting-debt small {
  display: block;
}

.transaction-accounting-card-header strong,
.transaction-accounting-debt strong {
  color: #3f4254;
  font-size: 12px;
}

.transaction-accounting-card-header span,
.transaction-accounting-debt span,
.transaction-accounting-debt small {
  margin-top: 2px;
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
}

.transaction-accounting-meta {
  gap: 12px;
  padding: 9px 13px;
  border-top: 1px solid #eff1f5;
  background: #f8f9fb;
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
}

.transaction-accounting-debts {
  border-top: 1px solid #eff1f5;
}

.transaction-accounting-debts-title {
  padding: 8px 13px;
  color: #a1a5b7;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.transaction-accounting-debt,
.transaction-accounting-advance {
  gap: 16px;
  padding: 10px 13px;
  border-top: 1px solid #eff1f5;
}

.transaction-accounting-debt-empty {
  padding: 10px 13px;
  border-top: 1px solid #eff1f5;
  color: #a1a5b7;
  font-size: 10px;
}

.transaction-accounting-advance {
  color: #3699ff;
  font-size: 11px;
  font-weight: 700;
}

.transaction-detail-empty--warning {
  border-color: #ffa800;
  color: #b77900;
}

.transaction-attempt-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 13px;
  border: 1px solid #e4e6ef;
  border-radius: 4px;
  background: #f8f9fb;
}

.transaction-attempt-card + .transaction-attempt-card {
  margin-top: 8px;
}

.transaction-attempt-card strong {
  display: block;
  color: #3f4254;
  font-size: 12px;
}

.transaction-attempt-description {
  margin-top: 4px;
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.45;
}

.transaction-detail-empty {
  padding: 14px;
  border: 1px dashed #e4e6ef;
  border-radius: 4px;
  color: #a1a5b7;
  font-size: 11px;
  text-align: center;
}

.transaction-detail-timeline {
  padding-left: 8px;
}

.transaction-detail-timeline-item {
  position: relative;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 10px;
  padding-bottom: 18px;
}

.transaction-detail-timeline-item::before {
  position: absolute;
  top: 13px;
  bottom: -2px;
  left: 6px;
  width: 1px;
  background: #e4e6ef;
  content: "";
}

.transaction-detail-timeline-item:last-child {
  padding-bottom: 0;
}

.transaction-detail-timeline-item:last-child::before {
  display: none;
}

.transaction-detail-timeline-dot {
  position: relative;
  z-index: 1;
  width: 13px;
  height: 13px;
  margin-top: 2px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  background: #a1a5b7;
  box-shadow: 0 0 0 1px #a1a5b7;
}

.transaction-detail-timeline-dot--success {
  background: #1bc5bd;
  box-shadow: 0 0 0 1px #1bc5bd;
}

.transaction-detail-timeline-dot--error {
  background: #f64e60;
  box-shadow: 0 0 0 1px #f64e60;
}

.transaction-detail-timeline-copy strong,
.transaction-detail-timeline-copy span {
  display: block;
}

.transaction-detail-timeline-copy strong {
  color: #3f4254;
  font-size: 12px;
  font-weight: 700;
}

.transaction-detail-timeline-copy span {
  margin-top: 3px;
  color: #7e8299;
  font-size: 11px;
  line-height: 1.4;
}

.transaction-detail-footer {
  flex: 0 0 auto;
  gap: 8px;
  padding: 12px 16px !important;
  border-top: 1px solid #eff1f5;
  background: #ffffff;
}

.transaction-detail-footer-note {
  color: #7e8299;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.35;
}

@media (max-width: 959px) {
  .transaction-segment-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 599px) {
  .transaction-segment-grid {
    grid-template-columns: 1fr;
  }

  .transaction-detail-footer-note {
    display: none;
  }
}
</style>

<style lang="scss">
@import "./payment-monitoring.scss";
</style>
